// Intent: "Siła prognozy" explainer (mock si_a_prognozy_finanse; Napper's
// schedule-strength screen): big score ring, 3-stage tracker (Uczenie się →
// Kalibrowanie → Dopracowywanie) and an honest "how it works" card — we only
// compute from the user's own entries, nothing is guessed.

import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ProgressRing } from '@/components/ui/progress-ring';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useBudgetStore } from '@/store/budget-store';
import { useFixedCostsStore } from '@/store/fixed-costs-store';
import { useGoalsStore } from '@/store/goals-store';
import { useIncomeStore } from '@/store/income-store';
import { colors, radius, spacing } from '@/theme/tokens';
import { forecastStrengthOf } from '@/utils/forecast';

const STAGES: { stage: 1 | 2 | 3; icon: keyof typeof Ionicons.glyphMap; titleKey: string; subKey: string }[] = [
  { stage: 1, icon: 'school-outline', titleKey: 'strength.stage1', subKey: 'strength.stage1Sub' },
  { stage: 2, icon: 'options-outline', titleKey: 'strength.stage2', subKey: 'strength.stage2Sub' },
  { stage: 3, icon: 'star-outline', titleKey: 'strength.stage3', subKey: 'strength.stage3Sub' },
];

export default function ForecastStrengthScreen() {
  const { t } = useTranslation();
  const sources = useIncomeStore((s) => s.sources);
  const incomeEntries = useIncomeStore((s) => s.entries);
  const fixedCosts = useFixedCostsStore((s) => s.costs);
  const budgetEntries = useBudgetStore((s) => s.entries);
  const goals = useGoalsStore((s) => s.goals);

  const { score, stage } = forecastStrengthOf(sources, incomeEntries, fixedCosts, budgetEntries, goals.length);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('strength.title')} />

        <View style={{ alignItems: 'center', gap: spacing.sm }}>
          <ProgressRing percent={score} size={190} strokeWidth={13}>
            <View style={{ alignItems: 'center' }}>
              <AppText variant="headlineLg" color={colors.navy} style={{ fontSize: 44, lineHeight: 50 }}>
                {score}
              </AppText>
              <AppText variant="bodyMd">{t('strength.title')}</AppText>
            </View>
          </ProgressRing>
          <AppText variant="headlineMd" style={{ textAlign: 'center' }}>
            {score >= 80 ? t('strength.headlineHigh') : t('strength.headlineLow')}
          </AppText>
          <AppText variant="bodyLg" style={{ textAlign: 'center' }}>
            {t('strength.subtitle')}
          </AppText>
        </View>

        {/* 3-stage tracker */}
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          {STAGES.map(({ stage: s, icon, titleKey, subKey }, idx) => {
            const reached = stage >= s;
            return (
              <View key={s} style={{ flex: 1, alignItems: 'center', gap: spacing.xs }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
                  <View style={{ flex: 1, height: 2, backgroundColor: idx === 0 ? 'transparent' : colors.primaryTile }} />
                  <View
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 23,
                      backgroundColor: reached ? colors.primary : colors.card,
                      borderWidth: reached ? 0 : 2,
                      borderColor: colors.outlineVariant,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Ionicons name={icon} size={20} color={reached ? '#FFFFFF' : colors.outline} />
                  </View>
                  <View style={{ flex: 1, height: 2, backgroundColor: idx === STAGES.length - 1 ? 'transparent' : colors.primaryTile }} />
                </View>
                <AppText variant="labelLg" style={{ textAlign: 'center' }}>
                  {t(titleKey)}
                </AppText>
                <AppText variant="labelMd" style={{ textAlign: 'center' }}>
                  {t(subKey)}
                </AppText>
              </View>
            );
          })}
        </View>

        {/* How it works (honest) */}
        <Card style={{ gap: spacing.sm }}>
          <AppText variant="headlineSm">{t('strength.howTitle')}</AppText>
          <AppText variant="bodyLg">{t('strength.howBody1')}</AppText>
          <AppText variant="bodyLg">{t('strength.howBody2')}</AppText>
        </Card>

        {/* What raises the score */}
        <Card style={{ gap: spacing.xs, backgroundColor: colors.infoBg }}>
          <AppText variant="labelLg" color={colors.navy}>
            {t('strength.boostTitle')}
          </AppText>
          {[t('strength.boost1'), t('strength.boost2'), t('strength.boost3'), t('strength.boost4')].map((line, i) => (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: spacing.xs }}>
              <Ionicons name="add-circle-outline" size={16} color={colors.primary} style={{ marginTop: 2 }} />
              <AppText variant="bodyMd" style={{ flex: 1 }}>
                {line}
              </AppText>
            </View>
          ))}
        </Card>
      </ScrollView>
    </SafeAreaView>
  );
}
